import {ChangeDetectionStrategy, Component, Input, OnChanges} from '@angular/core';
import {NgClass} from '@angular/common';
import {NbTooltipModule} from '@nebular/theme';
import {RouterLink} from '@angular/router';
import {TuiIcon} from '@taiga-ui/core';
import {BaseComponent, ProductTemplateFeatureValue, TemplateFeature} from 'compob2b-libs';

/** Чип части составной характеристики: текст + признак «не заполнено» + название характеристики. */
interface CompositeChip {
  text: string;
  empty: boolean;
  /** Название характеристики-источника (для части-ссылки) — подписывается над пустым чипом. */
  header?: string;
}

/**
 * Отображение составной (Composite) характеристики на карточке товара.
 *
 * Значение собирается из частей характеристики (`compositeParts`): часть-литерал
 * выводится как есть, часть-ссылка — значением соответствующей характеристики у
 * товара. Каждая часть — отдельный чип; незаполненная часть-ссылка подсвечивается
 * красным («Характеристика не заполнена»). Поле только для чтения.
 */
@Component({
  selector: 'app-tf-composite',
  templateUrl: './tf-composite.component.html',
  styleUrls: ['./tf-composite.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, NbTooltipModule, RouterLink, TuiIcon],
})
export class TfCompositeComponent extends BaseComponent implements OnChanges {
  @Input() feature: TemplateFeature;
  @Input() featureLink = false;
  /** Значения характеристик товара — для резолва частей-ссылок. */
  @Input() featureValues: ProductTemplateFeatureValue[] = [];

  /** Части состава в виде чипов (в порядке sort). */
  chips: CompositeChip[] = [];

  ngOnChanges(): void {
    this.buildChips();
  }

  /** Есть ли хотя бы одна незаполненная часть-ссылка. */
  hasEmptyPart(): boolean {
    return this.chips.some(chip => chip.empty);
  }

  private buildChips(): void {
    const parts = [...(this.feature?.compositeParts ?? [])].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
    this.chips = parts.map(part => {
      // часть-литерал — текст выводится как есть
      if (part.featureId == null) {
        const text = (part.value ?? '').trim();
        return {text, empty: text === ''};
      }
      // часть-ссылка — значение(я) этой характеристики у товара
      const header = part.featureHeader ?? undefined;
      const values = (this.featureValues ?? [])
        .filter(v => v.featureId === part.featureId && v.value != null && String(v.value).trim() !== '')
        .map(v => String(v.value).trim());
      return values.length
        ? {text: values.join(', '), empty: false, header}
        : {text: 'Характеристика не заполнена', empty: true, header};
    });
  }
}
